import { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldAlert, Trash2, VolumeX, Ban, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { YapService, NotificationService } from '../services/api';
import { useUserData } from './UserDataContext';
import { logAuditAction } from '../services/WatcherService';

interface LegionDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  yapId: string;
  yapContent: string;
  authorId: string;
  moderatorId: string;
  onDeleted?: () => void;
}

type MuteDuration = '1h' | '6h' | '12h' | '24h' | '3d' | '7d' | 'perm';

const DURATION_LABELS: Record<MuteDuration, string> = {
  '1h': '1 Hour',
  '6h': '6 Hours',
  '12h': '12 Hours',
  '24h': '24 Hours',
  '3d': '3 Days',
  '7d': '7 Days',
  'perm': 'Permanent'
};

export function LegionDeleteModal({
  isOpen,
  onClose,
  yapId,
  yapContent,
  authorId,
  moderatorId,
  onDeleted,
}: LegionDeleteModalProps) {
  const [reason, setReason] = useState('');
  const [mute, setMute] = useState(false);
  const [muteDuration, setMuteDuration] = useState<MuteDuration>('24h');
  const [ban, setBan] = useState(false);
  const { userProfile } = useUserData();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for deletion.');
      return;
    }

    setLoading(true);
    try {
      const { doc, updateDoc, Timestamp, serverTimestamp } = await import('firebase/firestore');
      const { getDbSafe } = await import('../services/firebaseConfig');
      const db = await getDbSafe();

      // 1. Delete the yap
      await YapService.deleteYapAsModerator(yapId, moderatorId, reason.trim());

      // 2. Audit Log
      const moderatorUsername = userProfile.username?.replace('@', '') || 'moderator';
      try {
        await logAuditAction({
          watcher_id: moderatorId,
          watcher_username: moderatorUsername,
          target_user_id: authorId,
          action_type: 'legion_delete',
          reason: reason.trim(),
          timestamp: serverTimestamp(),
          metadata: {
            yapId,
            content: yapContent.slice(0, 200),
            punishment: ban ? 'ban' : mute ? `mute_${muteDuration}` : 'none'
          }
        });
      } catch (auditErr) {
        console.warn('[LegionDeleteModal] Audit log failed:', auditErr);
      }

      // 3. Apply punishment if selected
      if (ban || mute) {
        try {
          
          let updateData: any = {
            status: ban ? 'banned' : 'muted',
          };

          if (mute && !ban) {
            let expiresAt = null;
            if (muteDuration !== 'perm') {
              const now = new Date();
              const hours = muteDuration.endsWith('h') ? parseInt(muteDuration) : 
                            muteDuration === '3d' ? 72 : 168;
              now.setHours(now.getHours() + hours);
              expiresAt = Timestamp.fromDate(now);
            }
            updateData.muteExpiresAt = expiresAt;
          }

          await updateDoc(doc(db, 'users', authorId), updateData);
        } catch (punishmentError) {
          console.error('[LegionDeleteModal] Failed to apply punishment:', punishmentError);
        }
      }

      // 3. Notify the author
      const preview = yapContent.length > 60 ? yapContent.substring(0, 60) + '…' : yapContent;
      let notifMessage = `Your post: "${preview}" was deleted because ${reason.trim()}.`;
      if (ban) {
        notifMessage += ' Additionally, your account has been Banned.';
      } else if (mute) {
        notifMessage += ` Additionally, your account has been Muted for ${DURATION_LABELS[muteDuration]}.`;
      }

      try {
        await NotificationService.sendNotification(
          authorId,
          'WATCHER_MOD',
          'Post Removed by Moderator',
          notifMessage,
          { 
            moderatorId, 
            yapId,
            fromUserName: 'AfroYap Moderation',
            fromUserAvatar: 'https://firebasestorage.googleapis.com/v0/b/afroyapp-630d7.appspot.com/o/system%2Fafroyap_mod_icon.png?alt=media', // Use moderation icon if exists or fallback
            isCritical: true
          }
        );
      } catch (notifError) {
        console.error('[LegionDeleteModal] Failed to send author notification:', notifError);
      }

      toast.success('Post deleted' + (ban ? ' & user banned' : mute ? ` & user muted (${muteDuration})` : ''));
      handleClose();
      onDeleted?.();
    } catch (err: any) {
      console.error('[LegionDeleteModal] Delete error:', err);
      toast.error(err.message || 'Failed to delete post.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReason('');
      setMute(false);
      setMuteDuration('24h');
      setBan(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={handleClose}
        hideClose
        className="p-0 overflow-hidden border border-white/10 rounded-[28px] shadow-2xl outline-none"
        style={{
          zIndex: 100000,
          backgroundColor: '#111111',
          width: '92vw',
          maxWidth: '380px',
        }}
      >
        {/* Header */}
        <div className="p-5 border-b border-white/10 flex items-center gap-3" style={{ backgroundColor: '#1a1a1a' }}>
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)' }}>
            <ShieldAlert className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white">Legion Moderation</h2>
            <p className="text-xs text-white/40">Remove post & optionally discipline user</p>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
          {/* Content preview */}
          {yapContent && (
            <div className="px-3 py-2 rounded-xl bg-white/5 border border-white/10">
              <p className="text-xs text-white/50 line-clamp-2">"{yapContent.substring(0, 120)}{yapContent.length > 120 ? '…' : ''}"</p>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="text-xs font-semibold text-white/60 uppercase tracking-widest mb-2 block">Reason (required)</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Violation of community guidelines, hate speech, spam..."
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none rounded-xl text-sm focus:ring-0"
              rows={3}
              maxLength={500}
            />
            <p className="text-[10px] text-white/30 text-right mt-1">{reason.length}/500</p>
          </div>

          {/* Punishment Toggles */}
          <div className="space-y-4">
            <label className="text-xs font-semibold text-white/60 uppercase tracking-widest block">Additional Punishment</label>

            <div className="space-y-2">
              {/* Mute */}
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => { setMute(!mute); if (!mute) setBan(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left ${mute ? 'border-amber-500/60 bg-amber-500/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
              >
                <VolumeX className={`w-4 h-4 shrink-0 ${mute ? 'text-amber-400' : 'text-white/40'}`} />
                <div className="flex-1">
                  <p className={`text-sm font-semibold ${mute ? 'text-amber-300' : 'text-white/70'}`}>Mute Account</p>
                  <p className="text-xs text-white/30">User can't post or reply</p>
                </div>
                <div className={`w-4 h-4 rounded-full border-2 ${mute ? 'bg-amber-400 border-amber-400' : 'border-white/20'}`} />
              </motion.button>

              {/* Mute Duration Selector */}
              <AnimatePresence>
                {mute && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    className="overflow-hidden bg-amber-500/5 rounded-xl border border-amber-500/20 p-3"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-3 h-3 text-amber-500/60" />
                      <span className="text-[10px] font-bold text-amber-500/60 uppercase tracking-tight">Mute Duration</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {(Object.keys(DURATION_LABELS) as MuteDuration[]).map((dur) => (
                        <button
                          key={dur}
                          onClick={() => setMuteDuration(dur)}
                          className={`px-2 py-2 rounded-lg text-[10px] font-bold transition-all border ${muteDuration === dur ? 'bg-amber-500 text-black border-amber-500' : 'bg-white/5 text-white/40 border-white/10 hover:bg-white/10'}`}
                        >
                          {dur === 'perm' ? '∞' : dur}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Ban */}
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => { setBan(!ban); if (!ban) setMute(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left ${ban ? 'border-red-500/60 bg-red-500/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
            >
              <Ban className={`w-4 h-4 shrink-0 ${ban ? 'text-red-400' : 'text-white/40'}`} />
              <div className="flex-1">
                <p className={`text-sm font-semibold ${ban ? 'text-red-300' : 'text-white/70'}`}>Ban Account</p>
                <p className="text-xs text-white/30">Full account suspension</p>
              </div>
              <div className={`w-4 h-4 rounded-full border-2 ${ban ? 'bg-red-400 border-red-400' : 'border-white/20'}`} />
            </motion.button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-3">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
            className="flex-1 text-white/50 hover:text-white hover:bg-white/5 rounded-xl border border-transparent"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !reason.trim()}
            className="flex-1 text-white rounded-xl flex items-center gap-2 disabled:opacity-40"
            style={{ backgroundColor: ban ? '#dc2626' : mute ? '#d97706' : '#3b82f6' }}
          >
            <Trash2 className="w-4 h-4" />
            {loading ? 'Wait...' : ban ? 'Delete & Ban' : mute ? 'Delete & Mute' : 'Delete Post'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
